import React, { useEffect, useState } from "react";
import { toast } from "./toast.js";
import { supabase } from "./supabaseClient.js";
import { useAuth } from "./auth.jsx";
import PlanPicker from "./PlanPicker.jsx";

const STATUS_AR = { trialing: ["تجريبي", "#eab308"], active: ["فعّال", "#34d399"], suspended: ["معلّق", "#f87171"], canceled: ["ملغى", "#94a3b8"] };
const PLAN_ICONS = { trial: "", basic: "", standard: "", pro: "", enterprise: "" };
const FEATS = [["accounting", "المحاسبة والمالية"], ["distribution", "التوزيع والتسويق"], ["exporting", "التصدير والتقارير"], ["activity_log", "سجل الدخول والمتابعة"]];
const fmt = (d) => { if (!d) return "-"; try { return new Date(d).toLocaleDateString("ar", { year: "numeric", month: "long", day: "numeric" }); } catch { return "-"; } };
const countdown = (end) => {
  if (!end) return null;
  let ms = new Date(end) - Date.now();
  if (ms <= 0) return { over: true };
  const d = Math.floor(ms / 86400000); ms -= d * 86400000;
  const h = Math.floor(ms / 3600000); ms -= h * 3600000;
  return { over: false, txt: d + " يوم · " + h + " ساعة · " + Math.floor(ms / 60000) + " دقيقة" };
};

function StorageBar() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { (async () => {
    try {
      const { supabase } = await import("./supabaseClient.js");
      const { data: u } = await supabase.auth.getUser();
      const { data: pr } = await supabase.from("profiles").select("org_id").eq("id", u.user.id).maybeSingle();
      if (!pr) return;
      const { data: sub } = await supabase.from("subscriptions").select("plans(storage_mb)").eq("org_id", pr.org_id).eq("status", "active").maybeSingle();
      const { data: us } = await supabase.from("org_storage").select("used_bytes").eq("org_id", pr.org_id).maybeSingle();
      setD({ limit: sub?.plans?.storage_mb ?? 500, used: Math.round((us?.used_bytes || 0) / 1048576 * 10) / 10 });
    } catch {}
  })(); }, []);
  if (!d) return null;
  const pct = Math.min(100, Math.round(d.used / d.limit * 100));
  const warn = pct >= 85;
  return (
    <div style={{ margin: "14px 0", padding: "12px 14px", background: "var(--surface)", border: "1px solid var(--line)" }}>
      <StorageBar />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, marginBottom: 7 }}>
        <span>مساحة التخزين</span>
        <span style={{ color: warn ? "#e23d3d" : "var(--faint)" }}>{d.used} / {d.limit} م.ب ({pct}%)</span>
      </div>
      <div style={{ height: 6, background: "var(--line)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: pct + "%", background: warn ? "#e23d3d" : "var(--accent)", transition: "width .4s" }} />
      </div>
    </div>
  );
}

export default function MySubscription({ onClose }) {
  const { me } = useAuth();
  const [sub, setSub] = useState(null);
  const [plan, setPlan] = useState(null);
  const [pays, setPays] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sent, setSent] = useState(false);
  const [npsVal, setNpsVal] = useState(null);
  const [npsTxt, setNpsTxt] = useState("");
  const [npsSent, setNpsSent] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const sendNps = async () => {
    try {
      const { data: prof } = await supabase.from("profiles").select("org_id").eq("id", me.id).maybeSingle();
      await supabase.from("nps_scores").insert({ org_id: prof && prof.org_id, from_id: me.id, score: npsVal, comment: npsTxt.trim() || null });
      setNpsSent(true);
    } catch (e) { try { toast("تعذر: " + (e && e.message || e), "err"); } catch {} }
  };
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 30000); return () => clearInterval(t); }, []);

  useEffect(() => { (async () => {
    try {
      const { data: prof } = await supabase.from("profiles").select("org_id").eq("id", me.id).maybeSingle();
      const org = prof && prof.org_id;
      if (!org) { setSub(false); return; }
      const { data: s } = await supabase.from("subscriptions").select("*").eq("org_id", org).maybeSingle();
      setSub(s || false);
      if (s && s.plan_id) { const { data: pl } = await supabase.from("plans").select("*").eq("id", s.plan_id).maybeSingle(); setPlan(pl || null); }
      const { data: ps } = await supabase.from("payments").select("*").eq("org_id", org).order("paid_at", { ascending: false }); setPays(ps || []);
      const { data: members } = await supabase.from("profiles").select("id,role,status").eq("org_id", org);
      const alive = (members || []).filter((m) => (m.status || "") !== "deleted");
      setUsage({ users: alive.length, pms: alive.filter((m) => m.role === "project_manager").length });
    } catch { setSub(false); setPays([]); }
  })(); }, [me && me.id]);

  const requestUpgrade = async () => {
    setBusy(true);
    try {
      const { data: prof } = await supabase.from("profiles").select("org_id").eq("id", me.id).maybeSingle();
      await supabase.from("tickets").insert({ org_id: prof && prof.org_id, from_id: me.id, from_name: me.fullName || "",
        subject: "طلب تجديد / ترقية اشتراك", body: "نرغب بتجديد أو ترقية اشتراك مؤسستنا - نرجو التواصل.", category: "مالي", priority: "عاجل" });
      setSent(true);
    } catch (e) { try { toast("تعذّر: " + (e && e.message || e), "err"); } catch {} }
    setBusy(false);
  };

  const printInv = (pay) => {
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) return;
    const cn = Number(pay.amount_kwd) < 0;
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>${pay.invoice_no || "فاتورة"}</title>
      <style>body{font-family:'Tajawal','Segoe UI',sans-serif;color:#0c1330;margin:0;padding:44px}
      .top{display:flex;justify-content:space-between;border-bottom:3px solid #14b8a6;padding-bottom:18px}
      h1{margin:0;font-size:24px}.muted{color:#667;font-size:13px}.no{font-size:20px;font-weight:800;color:#14b8a6}
      table{width:100%;border-collapse:collapse;margin-top:28px}th{background:#f0f6ff;padding:11px;text-align:right;font-size:13px;border-bottom:2px solid #d8e2f5}
      td{padding:12px 11px;border-bottom:1px solid #e5ecf8;font-size:14px}
      .total{margin-top:22px;text-align:left;font-size:19px;font-weight:900}.total span{color:${cn ? "#e23d3d" : "#14b8a6"}}
      .foot{margin-top:44px;padding-top:14px;border-top:1px solid #d8e2f5;font-size:12px;color:#667;text-align:center}
      @media print{body{padding:24px}}</style></head><body>
      <div class="top"><div><h1>نظام إدارة مشاريع الترجمة</h1><div class="muted">Tarjimtech - دولة الكويت</div></div>
      <div style="text-align:left"><div class="no">${pay.invoice_no || "-"}</div><div class="muted">${fmt(pay.paid_at)}</div></div></div>
      <table><tr><th>البيان</th><th>الباقة</th><th>المدة</th><th>الإجمالي</th></tr>
      <tr><td>${cn ? "مذكرة دائنة - استرداد" : "اشتراك المنصة"}</td><td>${pay.plan_id || "-"}</td><td>${pay.months || 0} شهر</td><td><b>${Number(pay.amount_kwd || 0).toLocaleString("en")} د.ك</b></td></tr></table>
      <div class="total">الإجمالي: <span>${Number(pay.amount_kwd || 0).toLocaleString("en")} د.ك</span></div>
      <div class="foot">${pay.note || ""} · صادرة آليًا من منصة tpms</div>
      <script>setTimeout(()=>window.print(),350)</` + `script></body></html>`);
    w.document.close();
  };

  const [stTxt, stColor] = sub && sub.status ? (STATUS_AR[sub.status] || [sub.status, "var(--faint)"]) : ["-", "var(--faint)"];
  const c = sub ? countdown(sub.period_end) : null;
  const box = { borderRadius: 14, border: "1px solid var(--line)", background: "var(--surface)", padding: "14px 16px", marginBottom: 12 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(12,19,48,.42)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", direction: "rtl", padding: 16 }} onClick={onClose}>
      <div className="tp-zoom" style={{ width: "min(620px, 96vw)", maxHeight: "88vh", overflowY: "auto", background: "color-mix(in srgb, var(--surface) 90%, transparent)", backdropFilter: "blur(14px)", border: "1px solid var(--line)", borderRadius: 20, padding: "24px 24px 20px", boxShadow: "0 22px 60px rgba(27,36,64,.22)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "var(--text)" }}> اشتراك المؤسسة</h3>
          <button className="ag-btn xs" onClick={onClose}>إغلاق</button>
        </div>

        {sub === null ? <div style={{ color: "var(--faint)", padding: 20 }}>جارٍ التحميل…</div> :
         sub === false ? <div style={{ color: "var(--faint)", padding: 20 }}>لا يوجد اشتراك مسجل - تواصلوا مع إدارة المنصة.</div> : (
          <>
            <div style={box}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text)" }}>{PLAN_ICONS[sub.plan_id] || ""} باقة {plan ? plan.name_ar : sub.plan_id}</div>
                <span style={{ fontSize: 12, fontWeight: 800, color: stColor, border: "1px solid " + stColor + "66", background: stColor + "16", borderRadius: 99, padding: "4px 13px" }}>{stTxt}</span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 2.1, color: "var(--text)", marginTop: 8 }}>
                {(() => {
                  const posPays = (pays || []).filter((x) => Number(x.amount_kwd) > 0);
                  const first = posPays.length ? posPays[posPays.length - 1] : null;
                  const total = (pays || []).reduce((a, x) => a + Number(x.amount_kwd || 0), 0);
                  return (
                    <>
                      مشتركون منذ: <b>{first ? fmt(first.paid_at) : fmt(sub.updated_at)}</b><br />
                      آخر تفعيل: <b>{fmt(sub.updated_at)}</b> · سارية حتى: <b>{fmt(sub.period_end)}</b><br />
                      إجمالي مدفوعاتكم: <b style={{ color: "var(--accent)" }}>{total.toLocaleString("en")} د.ك</b>
                    </>
                  );
                })()}
              </div>
              {c && !c.over && sub.status !== "canceled" && (
                <div style={{ marginTop: 8, borderRadius: 11, border: "1px solid var(--line)", background: "color-mix(in srgb, var(--accent) 6%, var(--surface))", padding: "9px 13px", fontSize: 13.5, fontWeight: 800 }}>
                   المتبقي: <span style={{ color: "var(--accent)" }}>{c.txt}</span>
                </div>
              )}
              {c && c.over && <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 800, color: "#e23d3d" }}> الاشتراك منتهٍ - جددوا لمواصلة العمل</div>}
            </div>

            {plan && (
              <div style={box}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--accent)", marginBottom: 10 }}>ما تشمله باقتكم</div>
                {FEATS.map(([k, l]) => {
                  const on = !!(plan.features && plan.features[k]);
                  return (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 9, padding: "5px 0", fontSize: 13, fontWeight: 700, color: on ? "var(--text)" : "var(--faint)" }}>
                      <span style={{ fontWeight: 900, color: on ? "#34d399" : "var(--faint)" }}>{on ? "✓" : "-"}</span>
                      <span style={{ opacity: on ? 1 : 0.65 }}>{l}</span>
                      {!on && <span style={{ fontSize: 10.5, color: "var(--faint)", border: "1px solid var(--line)", borderRadius: 99, padding: "2px 9px", marginInlineStart: "auto" }}>متاحة في الباقات الأعلى</span>}
                    </div>
                  );
                })}
                <div style={{ fontSize: 12, color: "var(--faint)", marginTop: 8, borderTop: "1px dashed var(--line)", paddingTop: 8 }}>
                  المقاعد: {plan.max_pms != null ? plan.max_pms + " مدير مشاريع" : "مدراء مشاريع بلا حد"} · {plan.max_users != null ? plan.max_users + " مستخدمًا" : "مستخدمون بلا حد"}
                </div>
              </div>
            )}

            {plan && usage && (
              <div style={box}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--accent)", marginBottom: 10 }}>الاستهلاك من حدود الباقة</div>
                {[["مدراء المشاريع", usage.pms, plan.max_pms], ["المستخدمون", usage.users, plan.max_users]].map(([l, v, mx], i) => (
                  <div key={i} style={{ marginBottom: 9 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>
                      <span>{l}</span><span style={{ color: mx != null && v >= mx ? "#e23d3d" : "var(--faint)" }}>{v}{mx != null ? " / " + mx : " (بلا حد)"}</span>
                    </div>
                    {mx != null && <div style={{ height: 6, borderRadius: 99, background: "var(--line)", overflow: "hidden" }}><div style={{ width: Math.min(100, (v / mx) * 100) + "%", height: "100%", borderRadius: 99, background: v >= mx ? "#e23d3d" : "var(--accent)" }} /></div>}
                  </div>
                ))}
                {plan && ((plan.max_pms != null && usage.pms >= plan.max_pms) || (plan.max_users != null && usage.users >= plan.max_users)) && (
                  <div style={{ fontSize: 12.5, color: "#b45309", fontWeight: 700 }}>بلغتم حد الباقة - الترقية تفتح مقاعد إضافية.</div>
                )}
              </div>
            )}

            <div style={box}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--accent)" }}>تجديد أو ترقية</div>
                {sent ? <span style={{ fontSize: 13, fontWeight: 800, color: "#34d399" }}>✓ وصل طلبكم - ستتواصل الإدارة قريبًا</span> :
                 <button onClick={() => setShowPicker(true)} style={{ fontFamily: "Tajawal, sans-serif", fontSize: 13.5, fontWeight: 800, padding: "9px 22px", borderRadius: 12, border: "none", cursor: "pointer", color: "var(--bg)", background: "var(--accent)" }}>الباقات والترقية</button>}
              </div>
            </div>

            <div style={box}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "var(--accent)", marginBottom: 10 }}>ما مدى احتمال أن ترشحوا المنصة لزميل؟</div>
              {npsSent ? <div style={{ fontSize: 13, fontWeight: 800, color: "#34d399" }}>شكرًا - تقييمكم وصل ويصنع الفرق.</div> : (
                <>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 9 }}>
                    {Array.from({ length: 11 }, (_, i) => (
                      <button key={i} onClick={() => setNpsVal(i)} style={{ width: 30, height: 30, borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 800,
                        border: "1px solid " + (npsVal === i ? "var(--accent)" : "var(--line)"),
                        background: npsVal === i ? "color-mix(in srgb, var(--accent) 12%, var(--surface))" : "transparent",
                        color: npsVal === i ? "var(--accent)" : "var(--text)" }}>{i}</button>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={npsTxt} onChange={(e) => setNpsTxt(e.target.value)} placeholder="ملاحظة اختيارية…"
                      style={{ flex: 1, fontFamily: "inherit", fontSize: 13, padding: "8px 12px", borderRadius: 10, border: "1px solid var(--line)", background: "color-mix(in srgb, var(--surface) 75%, transparent)", color: "var(--text)" }} />
                    <button disabled={npsVal === null} onClick={sendNps} style={{ fontFamily: "inherit", fontSize: 13, fontWeight: 800, padding: "8px 18px", borderRadius: 10, border: "none", cursor: "pointer", color: "var(--bg)", background: "var(--accent)", opacity: npsVal === null ? 0.5 : 1 }}>إرسال</button>
                  </div>
                </>
              )}
            </div>

            <div style={box}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "var(--accent)", marginBottom: 10 }}>فواتيركم ({pays ? pays.length : "…"})</div>
              {pays === null ? <div style={{ color: "var(--faint)", fontSize: 12.5 }}>جارٍ التحميل…</div> :
               pays.length === 0 ? <div style={{ color: "var(--faint)", fontSize: 12.5 }}>لا فواتير بعد.</div> :
               pays.map((p) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px dashed var(--line)", fontSize: 13 }}>
                  <span style={{ fontWeight: 800, color: Number(p.amount_kwd) < 0 ? "#e23d3d" : "var(--accent)" }}>{p.invoice_no || "-"}</span>
                  <span style={{ color: "var(--faint)" }}>{fmt(p.paid_at)}</span>
                  <b style={{ color: Number(p.amount_kwd) < 0 ? "#e23d3d" : "var(--text)" }}>{Number(p.amount_kwd || 0).toLocaleString("en")} د.ك</b>
                  <button className="ag-btn xs" onClick={() => printInv(p)}> طباعة</button>
                </div>
              ))}
            </div>      {showPicker && <PlanPicker onClose={() => setShowPicker(false)} currentPlan={sub && sub.plan_id} />}

          </>
        )}
      </div>
    </div>
  );
}
